import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'

import {
  createPlannerInventory,
  deletePlannerInventory,
  fetchCentre,
  fetchPlannerInventory
} from 'api'
import { showToast } from 'app/helpers'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

/** Tracked inventory per centre (mirrors the prototype's per-centre cards). */
const Inventory = () => {
  const [centres, setCentres] = useState([])
  const [inv, setInv] = useState({})
  const [draft, setDraft] = useState({})
  const [loading, setLoading] = useState(true)

  const refreshCentre = async (centreId) => {
    try {
      const data = await fetchPlannerInventory({ queries: { centre: centreId } })
      setInv((x) => ({ ...x, [centreId]: items(data) }))
    } catch (e) {
      // leave existing
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const list = items(await fetchCentre({}))
        setCentres(list)
        const entries = await Promise.all(
          list.map(async (c) => {
            try {
              const d = await fetchPlannerInventory({ queries: { centre: c.uuid } })
              return [c.uuid, items(d)]
            } catch (e) {
              return [c.uuid, []]
            }
          })
        )
        setInv(entries.reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {}))
      } catch (e) {
        showToast('Failed to load centres', 'danger')
      }
      setLoading(false)
    }
    load()
  }, [])

  const setField = (centreId, field, value) =>
    setDraft((x) => ({ ...x, [centreId]: { ...(x[centreId] || {}), [field]: value } }))

  const add = async (centreId) => {
    const d = draft[centreId] || {}
    if (!d.name) {
      showToast('Enter an item name', 'danger')
      return
    }
    try {
      await createPlannerInventory({
        centre: centreId,
        name: d.name,
        quantity: d.quantity || 1
      })
      setDraft((x) => ({ ...x, [centreId]: { name: '', quantity: 1 } }))
      await refreshCentre(centreId)
      showToast('Item added', 'success')
    } catch (e) {
      showToast('Failed to add item', 'danger')
    }
  }

  const del = async (centreId, id) => {
    try {
      await deletePlannerInventory({ ids: [id] })
      await refreshCentre(centreId)
    } catch (e) {
      showToast('Failed to remove item', 'danger')
    }
  }

  if (loading) {
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Tracked Inventory per Centre
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Add scarce or valuable materials so the system can flag conflicts. Common
        consumables (paper, paint) can stay as free-text on activities.
      </Typography>

      <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
        {centres.map((c) => (
          <Paper key={c.uuid} variant="outlined" style={{ padding: 12, width: 300 }}>
            <Typography variant="subtitle1">📍 {c.name}</Typography>
            {(inv[c.uuid] || []).length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No tracked items yet.
              </Typography>
            )}
            {(inv[c.uuid] || []).map((it) => (
              <Box
                key={it.uuid}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={0.5}
              >
                <Typography variant="body2">
                  {it.name} ({it.quantity})
                </Typography>
                <IconButton size="small" onClick={() => del(c.uuid, it.uuid)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box display="flex" alignItems="center" mt={1} style={{ gap: 6 }}>
              <TextField
                size="small"
                placeholder="Item name"
                value={(draft[c.uuid] || {}).name || ''}
                onChange={(e) => setField(c.uuid, 'name', e.target.value)}
              />
              <TextField
                size="small"
                type="number"
                style={{ width: 64 }}
                value={
                  (draft[c.uuid] || {}).quantity != null
                    ? (draft[c.uuid] || {}).quantity
                    : 1
                }
                onChange={(e) => setField(c.uuid, 'quantity', e.target.value)}
              />
              <Button size="small" variant="contained" onClick={() => add(c.uuid)}>
                Add
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  )
}

export default Inventory
